/**
 * Floodlight — a WebGL backdrop for the Now hero. Two soft stadium beams
 * sweep slowly across paper grain; the palette follows the match phase
 * (warm cream idle, gold on matchday, Arsenal red when the match is live).
 *
 * Dependency-free raw WebGL (~one fragment shader). Degrades to nothing —
 * the CSS behind it already looks right — when WebGL is unavailable, the
 * tab is hidden, or the user prefers reduced motion.
 */
import { useEffect, useRef } from 'react';
import type { MatchPhase } from '../lib/types';

const FRAG = `
precision mediump float;
uniform vec2 u_res;
uniform float u_t;
uniform vec3 u_tint;
uniform float u_energy;

float hash(vec2 p) {
  return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453);
}

float beam(vec2 uv, float angle, float width) {
  vec2 dir = vec2(cos(angle), sin(angle));
  float d = abs(dot(uv - vec2(0.5, 1.2), vec2(-dir.y, dir.x)));
  return smoothstep(width, 0.0, d);
}

void main() {
  vec2 uv = gl_FragCoord.xy / u_res;
  uv.x *= u_res.x / u_res.y;

  // Two counter-sweeping floodlight beams from above the frame.
  float a1 = 1.35 + 0.22 * sin(u_t * 0.11);
  float a2 = 1.75 + 0.26 * sin(u_t * 0.073 + 2.1);
  float b = beam(uv, a1, 0.34) * 0.6 + beam(uv, a2, 0.42) * 0.5;

  // Slow breathing glow anchored low-left (the pitch under lights).
  float glow = smoothstep(1.35, 0.0, distance(uv, vec2(0.18, 0.12)));
  glow *= 0.55 + 0.18 * sin(u_t * 0.21);

  // Paper grain so the light sits IN the page rather than on it.
  float g = hash(gl_FragCoord.xy + floor(u_t * 7.0)) * 0.05;

  float light = (b * 0.5 + glow * 0.6) * u_energy;
  gl_FragColor = vec4(u_tint * light + vec3(g * light), light * 0.85);
}
`;

const VERT = `
attribute vec2 a_pos;
void main() { gl_Position = vec4(a_pos, 0.0, 1.0); }
`;

const TINTS: Record<MatchPhase, [number, number, number]> = {
  idle: [0.72, 0.59, 0.24], // old gold
  pre: [0.86, 0.62, 0.16], // matchday gold, brighter
  live: [0.94, 0.05, 0.08], // Arsenal red
  ht: [0.94, 0.05, 0.08],
  ft: [0.78, 0.32, 0.1], // embers
};

const ENERGY: Record<MatchPhase, number> = { idle: 0.5, pre: 0.8, live: 1.0, ht: 0.8, ft: 0.7 };

export const Floodlight = ({ phase }: { phase: MatchPhase }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const phaseRef = useRef(phase);
  phaseRef.current = phase;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;

    const gl = canvas.getContext('webgl', { alpha: true, antialias: false, premultipliedAlpha: false });
    if (!gl) {
      canvas.remove();
      return;
    }
    canvas.addEventListener('webglcontextlost', () => canvas.remove(), { once: true });

    const compile = (type: number, src: string) => {
      const sh = gl.createShader(type);
      if (!sh) return null;
      gl.shaderSource(sh, src);
      gl.compileShader(sh);
      if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) return null;
      return sh;
    };
    const vs = compile(gl.VERTEX_SHADER, VERT);
    const fs = compile(gl.FRAGMENT_SHADER, FRAG);
    if (!vs || !fs) return;
    const prog = gl.createProgram();
    if (!prog) return;
    gl.attachShader(prog, vs);
    gl.attachShader(prog, fs);
    gl.linkProgram(prog);
    if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) return;
    gl.useProgram(prog);

    const buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
    const loc = gl.getAttribLocation(prog, 'a_pos');
    gl.enableVertexAttribArray(loc);
    gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);

    const uRes = gl.getUniformLocation(prog, 'u_res');
    const uT = gl.getUniformLocation(prog, 'u_t');
    const uTint = gl.getUniformLocation(prog, 'u_tint');
    const uEnergy = gl.getUniformLocation(prog, 'u_energy');

    gl.enable(gl.BLEND);
    gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);

    let raf = 0;
    let running = true;
    const t0 = performance.now();
    // Tint eases between phases so live mode fades in rather than snapping.
    let tint = [...TINTS[phaseRef.current]] as [number, number, number];
    let energy = ENERGY[phaseRef.current];

    const frame = (now: number) => {
      if (!running) return;
      const rect = canvas.getBoundingClientRect();
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const w = Math.max(1, Math.round(rect.width * dpr));
      const h = Math.max(1, Math.round(rect.height * dpr));
      if (canvas.width !== w || canvas.height !== h) {
        canvas.width = w;
        canvas.height = h;
        gl.viewport(0, 0, w, h);
      }
      const target = TINTS[phaseRef.current];
      const targetEnergy = ENERGY[phaseRef.current];
      tint = [
        tint[0] + (target[0] - tint[0]) * 0.04,
        tint[1] + (target[1] - tint[1]) * 0.04,
        tint[2] + (target[2] - tint[2]) * 0.04,
      ];
      energy += (targetEnergy - energy) * 0.04;

      gl.uniform2f(uRes, w, h);
      gl.uniform1f(uT, (now - t0) / 1000);
      gl.uniform3f(uTint, tint[0], tint[1], tint[2]);
      gl.uniform1f(uEnergy, energy);
      gl.clearColor(0, 0, 0, 0);
      gl.clear(gl.COLOR_BUFFER_BIT);
      gl.drawArrays(gl.TRIANGLES, 0, 3);
      raf = requestAnimationFrame(frame);
    };
    raf = requestAnimationFrame(frame);

    const onVisibility = () => {
      if (document.hidden) {
        running = false;
        cancelAnimationFrame(raf);
      } else if (!running) {
        running = true;
        raf = requestAnimationFrame(frame);
      }
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      running = false;
      cancelAnimationFrame(raf);
      document.removeEventListener('visibilitychange', onVisibility);
      gl.getExtension('WEBGL_lose_context')?.loseContext();
    };
  }, []);

  return <canvas ref={canvasRef} className="floodlight" aria-hidden="true" />;
};

/**
 * Goal burst — a one-shot 2D-canvas particle salvo (red + gold ticker tape)
 * fired when the score moves. Cheap, self-cleaning, reduced-motion aware.
 */
export function fireGoalBurst(host: HTMLElement | null): void {
  if (!host || window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
  const canvas = document.createElement('canvas');
  canvas.className = 'goal-burst';
  const rect = host.getBoundingClientRect();
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  canvas.width = rect.width * dpr;
  canvas.height = rect.height * dpr;
  host.appendChild(canvas);
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    canvas.remove();
    return;
  }
  ctx.scale(dpr, dpr);

  const COLORS = ['#EF0107', '#C4982A', '#F5E8C0', '#FFFFFF'];
  const parts = Array.from({ length: 90 }, () => ({
    x: rect.width * (0.3 + Math.random() * 0.4),
    y: rect.height * 0.55,
    vx: (Math.random() - 0.5) * 9,
    vy: -4 - Math.random() * 7,
    w: 3 + Math.random() * 4,
    h: 6 + Math.random() * 7,
    rot: Math.random() * Math.PI,
    vr: (Math.random() - 0.5) * 0.3,
    color: COLORS[Math.floor(Math.random() * COLORS.length)] ?? '#EF0107',
  }));

  const t0 = performance.now();
  const tick = (now: number) => {
    const t = (now - t0) / 1000;
    if (t > 1.6) {
      canvas.remove();
      return;
    }
    ctx.clearRect(0, 0, rect.width, rect.height);
    ctx.globalAlpha = Math.max(0, 1 - t / 1.6);
    for (const p of parts) {
      p.x += p.vx;
      p.y += p.vy;
      p.vy += 0.22;
      p.rot += p.vr;
      ctx.save();
      ctx.translate(p.x, p.y);
      ctx.rotate(p.rot);
      ctx.fillStyle = p.color;
      ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
      ctx.restore();
    }
    requestAnimationFrame(tick);
  };
  requestAnimationFrame(tick);
}

/** A respectful tap of haptic feedback where the platform offers it. */
export function haptic(ms = 8): void {
  try {
    navigator.vibrate?.(ms);
  } catch {
    /* not supported */
  }
}
